// What this device knows about its offline catalogue: which version is installed, what the edge
// offers, and the download in flight. The installed version is a pointer in localStorage that is
// only moved after the new file has been downloaded, hash-checked, stored and opened, so a failed
// or interrupted update leaves the old catalogue answering, and a swap is never half done.
//
// Updates come two ways (docs/design/catalogue-delta.md). A small increment (under
// CATALOGUE_SILENT_UPDATE_MAX_BYTES of compressed transfer) is fetched and applied to a copy in the
// background without asking, unless the browser says to save data or the connection is cellular;
// anything else, or a delta that fails, becomes an `offer` the person is asked about, as before.
// Lives on the main thread; the heavy work is in the worker (host.ts) behind a CatalogueBackend.
import { useSyncExternalStore } from "react";
import { z } from "zod";
import { isEdgeEnabled } from "../backends";
import { CatalogueWorkerError, createWorkerBackend } from "./client";
import { CatalogueFormatError, DownloadError } from "./errors";
import { brotliSupported, decideUpdate, fetchManifest, planInstall } from "./manifest";
import { updateBlocker, type UpdateBlocker } from "./network";
import type {
  CatalogueBackend,
  DeltaPlan,
  InstalledRef,
  InstallPlan,
  InstallProgress,
} from "./protocol";

const POINTER_KEY = "proef.catalogue";
/** How long a manifest check stays good; the catalogue is rebuilt nightly, so twice a day is plenty. */
const CHECK_EVERY_MS = 12 * 60 * 60 * 1000;
const CHECKED_KEY = "proef.catalogue.checked";
/** The newest version whose delta failed to apply: it is not tried again, the full bundle is offered. */
const DELTA_FAILED_KEY = "proef.catalogue.deltafailed";

/** What went wrong, for About to word; never a raw message. */
export type CatalogueErrorCode =
  | "offline"
  | "app-too-old"
  | "no-usable-file"
  | "download-failed"
  | "integrity"
  | "storage"
  | "no-space"
  | "open-failed";

/** The newest catalogue the edge offers, as far as a prompt or About needs to describe it. */
export interface CatalogueOffer {
  version: string;
  builtAt: string;
  /** Bytes to download. */
  bytes: number;
  /** Bytes the device needs while installing: the download plus the decoded file it becomes. */
  diskBytes: number;
}

export type CatalogueTask =
  | { kind: "checking" }
  | { kind: "downloading"; progress: InstallProgress | undefined }
  /** A small update being applied in the background; no question was asked. */
  | { kind: "updating"; progress: InstallProgress | undefined }
  | { kind: "removing" };

export interface CatalogueState {
  /** The catalogue this device holds, when it holds one. */
  readonly installed: InstalledRef | undefined;
  /** False once the installed file failed to open: lib/api.ts then behaves as if it were absent. */
  readonly usable: boolean;
  /** The edge's latest catalogue when it differs from the installed one (or none is installed). */
  readonly offer: CatalogueOffer | undefined;
  readonly task: CatalogueTask | undefined;
  readonly error: CatalogueErrorCode | undefined;
  /** Set when a small update is waiting because of Save-Data or a cellular connection. */
  readonly deferred: UpdateBlocker | undefined;
  /** What the browser says is left for this app, when it says (navigator.storage.estimate). */
  readonly freeBytes: number | undefined;
}

const InstalledRefSchema = z.object({
  version: z.string().min(1),
  builtAt: z.string(),
  file: z.string().min(1),
  store: z.enum(["opfs-sahpool", "idb"]),
  bytes: z.number().int().nonnegative(),
  updatedAt: z.string().optional(),
  updatedBy: z.enum(["download", "delta"]).optional(),
});

function readPointer(): InstalledRef | undefined {
  try {
    const raw = window.localStorage.getItem(POINTER_KEY);
    if (raw === null) return undefined;
    const parsed = InstalledRefSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    // Blocked storage or a damaged value: no catalogue, which is always a safe answer.
    return undefined;
  }
}

function writePointer(ref: InstalledRef | undefined): void {
  try {
    if (ref === undefined) window.localStorage.removeItem(POINTER_KEY);
    else window.localStorage.setItem(POINTER_KEY, JSON.stringify(ref));
  } catch {
    // Not persisting means the catalogue is offered again next session; nothing is lost but a download.
  }
}

const INITIAL: CatalogueState = {
  installed: undefined,
  usable: true,
  offer: undefined,
  task: undefined,
  error: undefined,
  deferred: undefined,
  freeBytes: undefined,
};

let state: CatalogueState =
  typeof window === "undefined" ? INITIAL : { ...INITIAL, installed: readPointer() };
const listeners = new Set<() => void>();
let backend: CatalogueBackend | undefined;
/** The pending open of the installed catalogue, shared by every first query. */
let opening: Promise<void> | undefined;
let busy: Promise<void> | undefined;
/** How long a failed on-demand download (ensureCatalogue) is remembered before a read may try again. */
const ENSURE_RETRY_MS = 60 * 1000;
/** When the last on-demand download failed. */
let ensureFailedAt: number | undefined;

function setState(patch: Partial<CatalogueState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => {
    listener();
  });
}

function getBackend(): CatalogueBackend {
  backend ??= createWorkerBackend();
  return backend;
}

export function getCatalogueState(): CatalogueState {
  return state;
}

export function subscribeCatalogue(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useCatalogueState(): CatalogueState {
  return useSyncExternalStore(subscribeCatalogue, getCatalogueState, () => INITIAL);
}

/** Whether queries can be answered from the device: a catalogue is installed and has not failed to open. */
export function isCatalogueReady(): boolean {
  return state.installed !== undefined && state.usable;
}

/** Room left under this origin's quota, or undefined where the browser does not say. */
async function estimateFree(): Promise<number | undefined> {
  try {
    const { quota, usage } = (await navigator.storage?.estimate?.()) ?? {};
    return quota === undefined || usage === undefined ? undefined : Math.max(0, quota - usage);
  } catch {
    return undefined;
  }
}

function errorCodeOf(error: unknown): CatalogueErrorCode {
  // The disk filled up mid-download: the worker drops the partial file, the old catalogue stays.
  if (error instanceof Error && error.name === "QuotaExceededError") return "no-space";
  if (error instanceof CatalogueWorkerError) {
    if (error.failure === "size" || error.failure === "digest" || error.failure === "decode")
      return "integrity";
    if (error.failure === "http") return "download-failed";
    if (error.problem !== undefined) return "open-failed";
    // fetch() inside the worker rejects with a TypeError when nothing answered.
    return error.name === "TypeError" ? "offline" : "storage";
  }
  if (error instanceof DownloadError) return "download-failed";
  if (error instanceof CatalogueFormatError) return "open-failed";
  // A fetch that never got an answer, or an edge that is switched off or unreachable.
  return "offline";
}

/** Opens the installed catalogue in the worker once; later calls share the result. */
function ensureOpen(): Promise<void> {
  const installed = state.installed;
  if (installed === undefined || !state.usable)
    return Promise.reject(new Error("no catalogue installed"));
  opening ??= getBackend()
    .open(installed)
    .then(() => undefined)
    .catch((error: unknown) => {
      opening = undefined;
      setState({ usable: false, error: "open-failed" });
      throw error;
    });
  return opening;
}

/**
 * Runs `read` against the open catalogue. Rejects when none is installed or it cannot be opened;
 * lib/api.ts treats either as "ask druif". The first call after a page load starts the worker.
 */
export async function withCatalogue<T>(
  read: (backend: CatalogueBackend) => Promise<T>,
): Promise<T> {
  await ensureOpen();
  return read(getBackend());
}

function rememberCheck(): void {
  try {
    window.localStorage.setItem(CHECKED_KEY, String(Date.now()));
  } catch {
    // Without it the next start simply checks again.
  }
}

function deltaFailedFor(version: string): boolean {
  try {
    return window.localStorage.getItem(DELTA_FAILED_KEY) === version;
  } catch {
    return false;
  }
}

function rememberDeltaFailure(version: string): void {
  try {
    window.localStorage.setItem(DELTA_FAILED_KEY, version);
  } catch {
    // Without it the delta is tried once more next check, which fails the same way and falls back again.
  }
}

function checkedRecently(now: number): boolean {
  try {
    const at = Number(window.localStorage.getItem(CHECKED_KEY));
    return Number.isFinite(at) && now - at < CHECK_EVERY_MS;
  } catch {
    return false;
  }
}

/** Records `plan` as the offer the person is asked about, or the error that makes it unusable. */
function offerFull(
  plan: InstallPlan | "app-too-old" | "no-usable-file",
  quiet: boolean,
): CatalogueOffer | undefined {
  if (typeof plan === "string") {
    setState({ offer: undefined, deferred: undefined, ...(quiet ? {} : { error: plan }) });
    return undefined;
  }
  setState({ offer: offerOf(plan), deferred: undefined });
  return state.offer;
}

function offerOf(plan: InstallPlan): CatalogueOffer {
  const diskBytes = plan.bytes + (plan.decodedBytes ?? 0);
  return { version: plan.version, builtAt: plan.builtAt, bytes: plan.bytes, diskBytes };
}

/** Pairs an offer with the room the device has for it, so the question can state both. */
async function withSpace(offer: CatalogueOffer | undefined): Promise<CatalogueOffer | undefined> {
  if (offer !== undefined) setState({ freeBytes: await estimateFree() });
  return offer;
}

/**
 * Moves the device to the version `ref` holds: opens it, then moves the pointer and sweeps the old
 * file. When the new file will not open, the old one (never touched) keeps answering and the new
 * one is removed. Resolves to whether the swap happened.
 */
async function adopt(worker: CatalogueBackend, ref: InstalledRef): Promise<boolean> {
  try {
    await worker.open(ref);
  } catch {
    await worker.prune(state.installed ?? null).catch(() => undefined);
    return false;
  }
  // The swap: from here the new version answers, and the old file is expendable.
  writePointer(ref);
  opening = Promise.resolve();
  setState({ installed: ref, usable: true, offer: undefined, deferred: undefined });
  await worker.prune(ref).catch(() => undefined);
  void navigator.storage?.persist?.().catch(() => undefined);
  return true;
}

/**
 * Applies a small update in the background, with no question asked. Whatever goes wrong (a download
 * that fails its checksum, a delta that does not reproduce the published digest, storage) leaves the
 * working catalogue untouched, is remembered so the same delta is not retried, and resolves to false:
 * the caller then offers the full bundle.
 */
async function updateByDelta(plan: DeltaPlan): Promise<boolean> {
  setState({
    task: { kind: "updating", progress: undefined },
    error: undefined,
    deferred: undefined,
  });
  const worker = getBackend();
  let applied = false;
  try {
    const ref = await worker.applyDelta(plan, (progress) => {
      setState({ task: { kind: "updating", progress } });
    });
    applied = await adopt(worker, {
      ...ref,
      updatedAt: new Date().toISOString(),
      updatedBy: "delta",
    });
  } catch {
    await worker.prune(state.installed ?? null).catch(() => undefined);
  } finally {
    setState({ task: undefined });
  }
  if (!applied) rememberDeltaFailure(plan.version);
  return applied;
}

/**
 * Asks the edge which catalogue it offers and acts on the answer. The same catalogue, or one a small
 * delta away, leaves nothing to ask about (the delta is applied right here); anything else is
 * recorded as `offer`. `quiet` is the background form: it skips when the edge is off, or when an
 * installed catalogue was checked in the last twelve hours, never surfaces an error, and holds a
 * delta back while Save-Data is on or the connection is cellular (then `deferred` says so and the
 * check is not counted, so the next app start tries again); so does the check About makes on
 * opening, which only informs. `userRequested`, the "check for a new version" button, applies a
 * small delta regardless of the connection. Resolves to the offer.
 */
export async function checkCatalogue({
  quiet = false,
  userRequested = false,
}: { quiet?: boolean; userRequested?: boolean } = {}): Promise<CatalogueOffer | undefined> {
  if (state.task !== undefined) return state.offer;
  // The throttle is for updates; a device with no catalogue yet is always told what is on offer.
  if (quiet && (!isEdgeEnabled() || (state.installed !== undefined && checkedRecently(Date.now()))))
    return state.offer;
  // A background check is invisible: no task, no error, nothing for About to flicker.
  if (!quiet) setState({ task: { kind: "checking" }, error: undefined });
  try {
    const decision = decideUpdate(await fetchManifest(), state.installed, brotliSupported());
    if (decision.kind === "delta") {
      const blocked = userRequested ? undefined : updateBlocker();
      if (blocked !== undefined) {
        setState({ offer: undefined, deferred: blocked });
        return undefined;
      }
      rememberCheck();
      if (!deltaFailedFor(decision.plan.version) && (await updateByDelta(decision.plan)))
        return undefined;
      return await withSpace(offerFull(decision.full, quiet));
    }
    rememberCheck();
    if (decision.kind === "current") {
      setState({ offer: undefined, deferred: undefined });
      return undefined;
    }
    return await withSpace(
      offerFull(decision.kind === "full" ? decision.plan : decision.kind, quiet),
    );
  } catch (error) {
    if (!quiet) setState({ error: errorCodeOf(error) });
    return undefined;
  } finally {
    if (!quiet) setState({ task: undefined });
  }
}

async function install(): Promise<void> {
  setState({ task: { kind: "downloading", progress: undefined }, error: undefined });
  try {
    // Asked at the opt-in, before the 50 MB arrive: a best-effort origin can be evicted under
    // pressure, and the estimate below is only worth trusting for storage that will be kept.
    await navigator.storage?.persist?.().catch(() => false);
    const plan = planInstall(await fetchManifest(), brotliSupported());
    if (typeof plan === "string") {
      setState({ error: plan });
      return;
    }
    const freeBytes = await estimateFree();
    setState({ freeBytes });
    if (freeBytes !== undefined && freeBytes < offerOf(plan).diskBytes) {
      setState({ error: "no-space" });
      return;
    }
    const worker = getBackend();
    const ref = await worker.install(plan, (progress) => {
      setState({ task: { kind: "downloading", progress } });
    });
    if (
      !(await adopt(worker, { ...ref, updatedAt: new Date().toISOString(), updatedBy: "download" }))
    )
      setState({ error: "open-failed" });
  } catch (error) {
    setState({ error: errorCodeOf(error) });
  } finally {
    setState({ task: undefined });
  }
}

/** Downloads, verifies and swaps in the edge's current catalogue. Never rejects: failures land in `state.error`. */
export function downloadCatalogue(): Promise<void> {
  busy ??= install().finally(() => {
    busy = undefined;
  });
  return busy;
}

/**
 * Makes sure a catalogue is on this device before a read that has no other source: resolves at once
 * when one is installed, joins a download in flight, else starts one unless the edge is off or the
 * last such download failed under a minute ago. Resolves to whether the device can now answer; never
 * rejects (the reason is in `state.error`).
 */
export async function ensureCatalogue(): Promise<boolean> {
  if (isCatalogueReady()) return true;
  // The catalogue comes from the edge: switched off, there is nobody to ask.
  if (!isEdgeEnabled()) return false;
  // Every read lands here, so a failed download is not retried on each one until the window passes.
  if (
    busy === undefined &&
    ensureFailedAt !== undefined &&
    Date.now() - ensureFailedAt < ENSURE_RETRY_MS
  )
    return false;
  await downloadCatalogue();
  ensureFailedAt = isCatalogueReady() ? undefined : Date.now();
  return isCatalogueReady();
}

/** Deletes the catalogue from this device. Searches go back to druif (when enabled). */
export async function removeCatalogue(): Promise<void> {
  if (state.task !== undefined) return;
  setState({ task: { kind: "removing" }, error: undefined });
  try {
    if (state.installed !== undefined) await getBackend().prune(null);
  } catch {
    // Orphaned files are swept by the next install; the pointer going away is what matters.
  }
  writePointer(undefined);
  opening = undefined;
  setState({ installed: undefined, usable: true, task: undefined });
}

/**
 * Test seam: swap the worker for `next` and forget everything held in memory. `installed` stands in
 * for the pointer a download would have saved (default: whatever storage holds).
 */
export function resetCatalogueForTests(next?: CatalogueBackend, installed?: InstalledRef): void {
  backend = next;
  opening = undefined;
  busy = undefined;
  ensureFailedAt = undefined;
  state = {
    ...INITIAL,
    installed: installed ?? (typeof window === "undefined" ? undefined : readPointer()),
  };
  listeners.forEach((listener) => {
    listener();
  });
}
